(() => {
  "use strict";

  const installationKey = "__masterKeyMemberFunnelV2";
  const storageKey = "masterkey_member_auth_v1";
  const markerVersion = 2;
  const markerMaxAgeMs = 30 * 60 * 1000;
  const clickDedupeMs = 2 * 1000;
  const publishTimeoutMs = 2500;

  if (window[installationKey]) return;
  window[installationKey] = true;

  let eventSequence = 0;
  let authReturnProcessing = false;

  function environment() {
    return window.Shopify?.theme?.role === "main" ? "production" : "staging";
  }

  function analyticsAllowed() {
    try {
      return window.Shopify?.customerPrivacy?.analyticsProcessingAllowed?.() === true;
    } catch {
      return false;
    }
  }

  function eventData(payload) {
    return {
      funnel_version: String(markerVersion),
      environment: environment(),
      ...payload,
    };
  }

  function createEventId(kind, timestamp = Date.now()) {
    eventSequence += 1;
    let randomToken = "";

    try {
      if (typeof window.crypto?.randomUUID === "function") {
        randomToken = window.crypto.randomUUID().replace(/-/gu, "");
      } else if (typeof window.crypto?.getRandomValues === "function") {
        const randomValues = new Uint32Array(2);
        window.crypto.getRandomValues(randomValues);
        randomToken = Array.from(randomValues, (value) => value.toString(36)).join("");
      }
    } catch {
      // A timestamp plus the per-page sequence remains the final fallback.
    }

    return [
      "mk",
      kind,
      Number(timestamp).toString(36),
      eventSequence.toString(36),
      randomToken,
    ].filter(Boolean).join("_");
  }

  function publishToShopify(name, data, onPublished = null) {
    if (!analyticsAllowed()) {
      return Promise.resolve({ status: "blocked" });
    }

    const publisher = window.Shopify?.analytics?.publish;
    if (typeof publisher !== "function") {
      return Promise.resolve({ status: "retryable" });
    }

    let result;
    try {
      result = publisher.call(window.Shopify.analytics, name, data);
    } catch {
      return Promise.resolve({ status: "unknown" });
    }

    if (!result || typeof result.then !== "function") {
      if (result === false) {
        return Promise.resolve({ status: "retryable" });
      }

      onPublished?.();
      return Promise.resolve({ status: "published" });
    }

    return new Promise((resolve) => {
      let outcomeSettled = false;
      const timeoutId = window.setTimeout(() => {
        if (outcomeSettled) return;
        outcomeSettled = true;
        resolve({ status: "unknown" });
      }, publishTimeoutMs);

      Promise.resolve(result).then((published) => {
        if (published !== false) {
          // Keep this callback active after a timeout: a late positive receipt is
          // still authoritative and can safely complete the stored marker.
          onPublished?.();
        }

        if (outcomeSettled) return;
        outcomeSettled = true;
        window.clearTimeout(timeoutId);
        resolve({ status: published === false ? "retryable" : "published" });
      }).catch(() => {
        if (outcomeSettled) return;
        outcomeSettled = true;
        window.clearTimeout(timeoutId);
        // A rejection can be ambiguous after the browser handed the event to
        // Shopify, so do not retry it automatically and risk a duplicate.
        resolve({ status: "unknown" });
      });
    });
  }

  function dispatchInternalEvent(name, data) {
    try {
      window.dispatchEvent(new CustomEvent("masterkey:funnel-event", {
        detail: { name, data },
      }));
    } catch {
      // The internal event is an optional debugging hook.
    }
  }

  function saveMarker(marker) {
    try {
      window.sessionStorage.setItem(storageKey, JSON.stringify(marker));
    } catch {
      // Storage can be unavailable in strict privacy modes.
    }
  }

  function readMarker() {
    try {
      const value = window.sessionStorage.getItem(storageKey);
      return value ? JSON.parse(value) : null;
    } catch {
      return null;
    }
  }

  function clearMarker() {
    try {
      window.sessionStorage.removeItem(storageKey);
    } catch {
      // Storage can be unavailable in strict privacy modes.
    }
  }

  function isCartPath(pathname) {
    return pathname === "/cart";
  }

  function isCartMarker(marker) {
    return marker?.context === "cart"
      && marker.return_path === "/cart"
      && ["cart_page", "cart_drawer"].includes(marker.source)
      && marker.product_id === undefined
      && marker.product_handle === undefined;
  }

  function isValidMarker(marker) {
    return Boolean(
      marker
      && marker.version === markerVersion
      && (isCartMarker(marker) || (
        marker.context === undefined
        && typeof marker.product_id === "string"
        && typeof marker.product_handle === "string"
      ))
      && typeof marker.source === "string"
      && Number.isFinite(Number(marker.started_at))
      && typeof marker.cta_event_id === "string"
      && marker.cta_event_id !== ""
      && typeof marker.cta_status === "string",
    );
  }

  function updateMarker(ctaEventId, updater) {
    if (!analyticsAllowed()) return null;

    const currentMarker = readMarker();
    if (!isValidMarker(currentMarker) || currentMarker.cta_event_id !== ctaEventId) {
      return null;
    }

    updater(currentMarker);
    saveMarker(currentMarker);
    return currentMarker;
  }

  function clearCompletedMarker(ctaEventId) {
    if (!analyticsAllowed()) return;

    const currentMarker = readMarker();
    if (!isValidMarker(currentMarker) || currentMarker.cta_event_id !== ctaEventId) {
      return;
    }

    if (currentMarker.cta_status === "published" && currentMarker.auth_status === "published") {
      clearMarker();
    }
  }

  function recordOutcome(ctaEventId, field, outcome) {
    updateMarker(ctaEventId, (currentMarker) => {
      if (currentMarker[field] !== "published") {
        currentMarker[field] = outcome.status;
      }
    });
    clearCompletedMarker(ctaEventId);
  }

  function publishCta(marker, { dispatchInternal = false, replayedAfterAuth = false } = {}) {
    const data = eventData({
      event_id: marker.cta_event_id,
      ...(isCartMarker(marker) ? { context: "cart" } : {
        product_id: marker.product_id,
        product_handle: marker.product_handle,
      }),
      source: marker.source,
      ...(replayedAfterAuth ? { replayed_after_auth: true } : {}),
    });

    if (dispatchInternal) {
      dispatchInternalEvent("masterkey:member_cta_clicked", data);
    }

    return publishToShopify("masterkey:member_cta_clicked", data, () => {
      updateMarker(marker.cta_event_id, (currentMarker) => {
        currentMarker.cta_status = "published";
      });
      clearCompletedMarker(marker.cta_event_id);
    }).then((outcome) => {
      recordOutcome(marker.cta_event_id, "cta_status", outcome);
      return outcome;
    });
  }

  function publishAuthReturn(marker, markerAge, currentHandle) {
    const data = eventData({
      event_id: marker.auth_event_id,
      ...(isCartMarker(marker) ? { context: "cart" } : {
        product_id: marker.product_id || document.body.dataset.mkProductId || "",
        product_handle: currentHandle,
      }),
      source: marker.source || "unknown",
      elapsed_seconds: Math.round(markerAge / 1000),
    });

    if (marker.auth_internal_dispatched !== true) {
      dispatchInternalEvent("masterkey:member_auth_returned", data);
      updateMarker(marker.cta_event_id, (currentMarker) => {
        currentMarker.auth_internal_dispatched = true;
      });
    }

    return publishToShopify("masterkey:member_auth_returned", data, () => {
      updateMarker(marker.cta_event_id, (currentMarker) => {
        currentMarker.auth_status = "published";
      });
      clearCompletedMarker(marker.cta_event_id);
    }).then((outcome) => {
      recordOutcome(marker.cta_event_id, "auth_status", outcome);
      return outcome;
    });
  }

  function cartCtaContext(target) {
    const cta = target.closest('[data-mk-cart-member-gate][data-reason="login-required"] a[href]');
    if (!cta) return null;

    const source = cta.closest("cart-drawer") ? "cart_drawer"
      : isCartPath(window.location?.pathname) ? "cart_page" : "";
    if (!source) return null;

    try {
      const loginUrl = new URL(cta.getAttribute("href"), window.location.origin);
      if (loginUrl.origin !== window.location.origin
        || loginUrl.pathname !== "/customer_authentication/login"
        || loginUrl.searchParams.getAll("return_to").length !== 1
        || !isCartPath(loginUrl.searchParams.get("return_to"))) return null;
    } catch {
      return null;
    }

    // Store only the allowlisted destination, never an authentication URL,
    // cart token, query string, customer detail, or product inferred from a cart.
    return { context: "cart", return_path: "/cart", source };
  }

  document.addEventListener("click", (event) => {
    if (!(event.target instanceof Element)) return;

    const cta = event.target.closest("[data-mk-member-cta]");
    const cartContext = cta ? null : cartCtaContext(event.target);
    if ((!cta && !cartContext) || !analyticsAllowed()) return;

    const targetContext = cartContext || {
      product_id: cta.dataset.productId || "",
      product_handle: cta.dataset.productHandle || "",
      source: cta.dataset.source || "unknown",
    };
    const startedAt = Date.now();
    const existingMarker = readMarker();

    if (
      isValidMarker(existingMarker)
      && existingMarker.context === targetContext.context
      && existingMarker.return_path === targetContext.return_path
      && existingMarker.product_id === targetContext.product_id
      && existingMarker.product_handle === targetContext.product_handle
      && existingMarker.source === targetContext.source
      && startedAt - Number(existingMarker.started_at) >= 0
      && startedAt - Number(existingMarker.started_at) <= clickDedupeMs
    ) {
      return;
    }

    const marker = {
      version: markerVersion,
      ...targetContext,
      started_at: startedAt,
      cta_event_id: createEventId("cta", startedAt),
      cta_status: "in_flight",
      auth_event_id: "",
      auth_status: "not_started",
      auth_internal_dispatched: false,
    };

    saveMarker(marker);
    publishCta(marker, { dispatchInternal: true });
  }, true);

  function processAuthReturn() {
    if (authReturnProcessing || !analyticsAllowed()) return;

    const marker = readMarker();
    if (!marker) return;
    if (!isValidMarker(marker)) {
      clearMarker();
      return;
    }

    const markerAge = Date.now() - Number(marker.started_at);
    const currentHandle = document.body.dataset.mkProductHandle || "";
    const isAuthenticated = document.body.dataset.mkCustomerAuthenticated === "true";
    const isFresh = Number.isFinite(markerAge) && markerAge >= 0 && markerAge <= markerMaxAgeMs;
    const matchesTarget = isCartMarker(marker)
      ? isCartPath(window.location?.pathname)
      : currentHandle !== "" && currentHandle === marker.product_handle;

    if (!isFresh) {
      clearMarker();
      return;
    }

    if (!isAuthenticated || !matchesTarget) return;

    authReturnProcessing = true;
    const jobs = [];

    // Only a confirmed `false`/unavailable result is retried. An in-flight,
    // rejected, or timed-out call is not replayed because its delivery is
    // ambiguous and a second publish could double-count the event.
    if (marker.cta_status === "retryable") {
      updateMarker(marker.cta_event_id, (currentMarker) => {
        currentMarker.cta_status = "in_flight";
      });
      marker.cta_status = "in_flight";
      jobs.push(publishCta(marker, { replayedAfterAuth: true }));
    }

    if (marker.auth_status === "not_started" || marker.auth_status === "retryable") {
      if (!marker.auth_event_id) {
        marker.auth_event_id = createEventId("auth");
      }
      marker.auth_status = "in_flight";
      updateMarker(marker.cta_event_id, (currentMarker) => {
        currentMarker.auth_event_id = marker.auth_event_id;
        currentMarker.auth_status = "in_flight";
      });
      jobs.push(publishAuthReturn(marker, markerAge, currentHandle));
    }

    Promise.all(jobs).finally(() => {
      authReturnProcessing = false;
      clearCompletedMarker(marker.cta_event_id);
    });
  }

  document.addEventListener("visitorConsentCollected", () => {
    if (analyticsAllowed()) {
      processAuthReturn();
    } else {
      // A consent decision that does not allow analytics invalidates any
      // tracking-only marker created while consent was previously granted.
      clearMarker();
    }
  });

  if (typeof window.Shopify?.customerPrivacy?.analyticsProcessingAllowed === "function") {
    processAuthReturn();
  } else if (typeof window.Shopify?.loadFeatures === "function") {
    try {
      window.Shopify.loadFeatures([{
        name: "consent-tracking-api",
        version: "0.1",
      }], (error) => {
        if (error) return;
        processAuthReturn();
      });
    } catch {
      // Fail closed: clicks remain functional, but no marker or event is used.
    }
  }
})();
